import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@this/shared/ui/feedbacks/modal';
import { Button } from '@this/shared/ui/inputs/button';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import { first } from '@this/src/util';
import type { ReservationInfo, SelectOption } from '../api/reservationInfo';
import { updateTripApplicationInfo } from '../api/tripApplicationInfo';

type FormValues = {
  finalDestination: string;
  purpose: string;
  internalNumber: string;
  chargingDepartmentId: number | null;
  projectId: number | null;
};

type FormErrors = Partial<Record<keyof FormValues, string>>;

interface Props {
  tripId: string;
  trip: TripResponseArgs;
  reservationInfo: ReservationInfo;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const TripApplicationEditModal = ({ tripId, trip, reservationInfo, open, onClose, onSuccess }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { settingFlags, approvalRequired, options } = reservationInfo;

  const [values, setValues] = useState<FormValues>(() => ({
    finalDestination: trip.final_destination || '',
    purpose: trip.purpose || '',
    internalNumber: trip.internal_number || '',
    chargingDepartmentId: first(trip.charging_departments || [])?.id || null,
    projectId: first(trip.projects || [])?.id || null
  }));

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValues({
        finalDestination: trip.final_destination || '',
        purpose: trip.purpose || '',
        internalNumber: trip.internal_number || '',
        chargingDepartmentId: first(trip.charging_departments || [])?.id || null,
        projectId: first(trip.projects || [])?.id || null
      });
      setErrors({});
      setSubmitError(null);
    }
  }, [open, trip]);

  const departments = useMemo(
    () => options.departments.map((d): SelectOption => ({ id: d.id, name: d.name })),
    [options.departments]
  );

  const handleTextChange = useCallback(
    (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValues(prev => ({ ...prev, [field]: newValue }));
      setErrors(prev => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const handleSelectChange = useCallback(
    (field: 'chargingDepartmentId' | 'projectId') => (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value ? Number(e.target.value) : null;
      setValues(prev => ({ ...prev, [field]: newValue }));
      setErrors(prev => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const validate = useCallback((): FormErrors => {
    const newErrors: FormErrors = {};
    const destinationRequired = settingFlags.isDestinationBusinessTripRequired || approvalRequired;
    const purposeRequired = settingFlags.isPurposeBusinessTripRequired || approvalRequired;

    if (destinationRequired && !values.finalDestination.trim()) {
      newErrors.finalDestination = '出張先を入力してください';
    }
    if (purposeRequired && !values.purpose.trim()) {
      newErrors.purpose = '出張の目的を入力してください';
    }
    if (settingFlags.isInternalNumberRequired && !values.internalNumber.trim()) {
      newErrors.internalNumber = '社内管理番号を入力してください';
    }
    if (settingFlags.isChargingDepartmentRequired && !values.chargingDepartmentId) {
      newErrors.chargingDepartmentId = '費用負担部署を選択してください';
    }
    if (settingFlags.isProjectNameRequired && !values.projectId) {
      newErrors.projectId = 'プロジェクトを選択してください';
    }
    return newErrors;
  }, [values, settingFlags, approvalRequired]);

  const handleSubmit = useCallback(async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await updateTripApplicationInfo(tripId, {
        finalDestination: values.finalDestination,
        purpose: values.purpose,
        internalNumber: values.internalNumber,
        chargingDepartmentId: values.chargingDepartmentId,
        projectId: values.projectId
      });

      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setSubmitError(result.error || '保存に失敗しました');
      }
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  }, [tripId, values, validate, onSuccess, onClose]);

  const renderTextInput = (
    field: keyof FormValues,
    label: string,
    placeholder: string,
    isRequired: boolean,
    isShown: boolean
  ) => {
    if (!isShown) return null;
    const inputId = `trip-application-${field}`;
    return (
      <div style={styles.formGroup}>
        <label htmlFor={inputId} style={styles.label}>
          {label}
          {isRequired && <span style={styles.required}>*</span>}
        </label>
        <input
          id={inputId}
          type="text"
          style={{
            ...styles.input,
            ...(errors[field] ? styles.inputError : {})
          }}
          value={String(values[field] ?? '')}
          placeholder={placeholder}
          onChange={handleTextChange(field)}
          disabled={isSubmitting}
        />
        {errors[field] && <span style={styles.errorText}>{errors[field]}</span>}
      </div>
    );
  };

  const renderSelectInput = (
    field: 'chargingDepartmentId' | 'projectId',
    label: string,
    selectOptions: SelectOption[],
    isRequired: boolean,
    isShown: boolean
  ) => {
    if (!isShown) return null;
    const inputId = `trip-application-${field}`;
    return (
      <div style={styles.formGroup}>
        <label htmlFor={inputId} style={styles.label}>
          {label}
          {isRequired && <span style={styles.required}>*</span>}
        </label>
        <select
          id={inputId}
          style={{
            ...styles.select,
            ...(errors[field] ? styles.inputError : {})
          }}
          value={values[field] ?? ''}
          onChange={handleSelectChange(field)}
          disabled={isSubmitting}
        >
          <option value="">選択してください</option>
          {selectOptions.map(option => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        {errors[field] && <span style={styles.errorText}>{errors[field]}</span>}
      </div>
    );
  };

  const destinationRequired = settingFlags.isDestinationBusinessTripRequired || approvalRequired;
  const purposeRequired = settingFlags.isPurposeBusinessTripRequired || approvalRequired;

  return (
    <Modal size="small" open={open} onClose={onClose}>
      <ModalHeader>出張申請情報の編集</ModalHeader>
      <ModalBody>
        <div style={styles.formContent}>
          {renderTextInput(
            'finalDestination',
            '出張先',
            '出張先を入力してください',
            destinationRequired,
            settingFlags.showDestinationBusinessTrip
          )}
          {renderTextInput(
            'purpose',
            '出張の目的',
            '例：クライアント訪問',
            purposeRequired,
            settingFlags.showPurposeBusinessTrip
          )}
          {renderTextInput(
            'internalNumber',
            '社内管理番号',
            '例：20250116-001',
            settingFlags.isInternalNumberRequired,
            settingFlags.showInternalNumber
          )}
          {renderSelectInput(
            'chargingDepartmentId',
            '費用負担部署',
            departments,
            settingFlags.isChargingDepartmentRequired,
            settingFlags.showChargingDepartment
          )}
          {renderSelectInput(
            'projectId',
            'プロジェクト（案件）',
            options.projects,
            settingFlags.isProjectNameRequired,
            settingFlags.showProjectName
          )}
          {submitError && <Alert variant="danger">{submitError}</Alert>}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="sub" onClick={onClose} disabled={isSubmitting}>
          キャンセル
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? '保存中...' : '保存'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const getStyles = (theme: AITTheme) => ({
  formContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px'
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.textColor || '#374151'
  },
  required: {
    color: '#ef4444',
    marginLeft: '4px'
  },
  input: {
    padding: '8px 12px',
    fontSize: '14px',
    border: `1px solid ${theme.grayBorderColor || '#e5e7eb'}`,
    borderRadius: '6px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const
  },
  select: {
    padding: '8px 12px',
    fontSize: '14px',
    border: `1px solid ${theme.grayBorderColor || '#e5e7eb'}`,
    borderRadius: '6px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
    backgroundColor: 'white'
  },
  inputError: {
    borderColor: '#ef4444'
  },
  errorText: {
    fontSize: '12px',
    color: '#ef4444'
  }
});

export default TripApplicationEditModal;
